import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  bulkRemoveSecondaryDomains,
  UserKeys,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { Dispatch, SetStateAction, useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { PossibleDomain } from '../../../../../types';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteSecondaryDomainsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: PossibleDomain[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
}

export default function DeleteSecondaryDomainsDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
}: DeleteSecondaryDomainsDialogProps) {
  const { createEvent } = useAnalytics();
  const { changeLogRoles } = useUserSettings();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const selectedDomains = selectedRows.map((row) => row.domain);
  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.UpdateSecondaryDomains.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      bulkRemoveSecondaryDomains(selectedDomains, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Secondary domains disabled successfully!',
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.SecondaryDomains],
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.Domains],
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.UserSettings],
      });

      setSelectedRows([]);
      setChangeLogMessage('');

      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue disabling the secondary domains. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.settings.disable_secondary_domain, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.settings,
      });
      setOpenChangeLogDialog(false);
    },
  });

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        selectedRows.length === 1
          ? 'Disable Secondary Domain'
          : 'Disable Secondary Domains'
      }
      dialogContent={
        <Box>
          <Typography>
            {selectedRows.length > 1 ? (
              <>
                Do you wish to disable <strong>{selectedRows.length}</strong>{' '}
                secondary domains?
              </>
            ) : selectedRows.length ? (
              <>
                Do you wish to disable the secondary domain{' '}
                <strong>{selectedRows[0].domain}</strong>?
              </>
            ) : (
              ''
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: selectedRows.length === 1 ? 'Disable Domain' : 'Disable Domains',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'warning',
      }}
    />
  );
}
